import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlass,
  Fire,
  Sparkle,
  Globe,
  Headphones,
  Heart,
  Play,
  Pause,
} from '@phosphor-icons/react';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import { Input } from '../components/ui/input';
import { motion } from 'framer-motion';

const StationCard = ({
  station,
  onClick,
  isFavorite,
  onFavorite,
  onPlay,
  isCurrentlyPlaying,
  t,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      data-testid={`station-card-${station.station_id}`}
      className="bg-[#0A0E17] border border-white/5 rounded-2xl p-6 hover:border-cyan-500/50 hover:shadow-[0_0_20px_rgba(0,255,255,0.15)] hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
      onClick={onClick}
    >
      <div className="aspect-square rounded-xl overflow-hidden mb-4 relative bg-[#0A0E17]">
        <img
          src={
            station.logo_url ||
            'https://images.unsplash.com/photo-1773053525998-8cb667020fa7?w=400'
          }
          alt={station.name}
          onError={(e) => {
            e.target.src =
              'https://images.unsplash.com/photo-1773053525998-8cb667020fa7?w=400';
          }}
          className="w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-4">
          <button
            data-testid={`play-btn-${station.station_id}`}
            onClick={(e) => {
              e.stopPropagation();
              onPlay(station);
            }}
            className="w-14 h-14 rounded-full bg-cyan-500 hover:bg-cyan-400 text-black flex items-center justify-center hover:shadow-[0_0_20px_rgba(0,255,255,0.5)] transition-all"
          >
            {isCurrentlyPlaying ? (
              <Pause size={24} weight="fill" />
            ) : (
              <Play size={24} weight="fill" />
            )}
          </button>
        </div>

        {isCurrentlyPlaying && (
          <div className="absolute top-3 right-3 bg-cyan-500 text-black px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
            <span className="inline-flex gap-0.5">
              <span
                className="w-0.5 h-2 bg-black rounded animate-pulse"
                style={{ animationDelay: '0ms' }}
              ></span>
              <span
                className="w-0.5 h-2 bg-black rounded animate-pulse"
                style={{ animationDelay: '150ms' }}
              ></span>
              <span
                className="w-0.5 h-2 bg-black rounded animate-pulse"
                style={{ animationDelay: '300ms' }}
              ></span>
            </span>
            LIVE
          </div>
        )}
      </div>

      <div className="flex items-start justify-between mb-2">
        <h3 className="text-lg font-semibold text-white group-hover:text-cyan-400 transition-colors line-clamp-1">
          {station.name}
        </h3>

        <button
          data-testid={`favorite-btn-${station.station_id}`}
          onClick={(e) => {
            e.stopPropagation();
            onFavorite(station.station_id);
          }}
          className="text-gray-400 hover:text-cyan-400 transition-colors"
        >
          <Heart
            size={20}
            weight={isFavorite ? 'fill' : 'regular'}
            className={isFavorite ? 'text-cyan-400' : ''}
          />
        </button>
      </div>

      <p className="text-sm text-gray-400 mb-2">
        {station.country} • {station.genre}
      </p>

      <div className="flex items-center gap-2 text-xs text-gray-500">
        <Headphones size={14} />
        <span>
          {station.play_count?.toLocaleString() || 0} {t.plays}
        </span>
      </div>
    </motion.div>
  );
};

const Discover = () => {
  const [stations, setStations] = useState([]);
  const [filteredStations, setFilteredStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [genres, setGenres] = useState([]);
  const [favorites, setFavorites] = useState([]);

  const navigate = useNavigate();
  const { user } = useAuth();
  const { playStation, currentStation, isPlaying } = useAudioPlayer();
  const { t } = useLanguage();

  useEffect(() => {
    loadData();
  }, [selectedGenre]);

  useEffect(() => {
    filterStations();
  }, [search, stations]);

  const loadData = async () => {
    try {
      const params = { sort: 'popularity', limit: 30 };

      if (selectedGenre !== 'all') {
        params.genre = selectedGenre;
      }

      const stationsRes = await api.getStations(params);
      setStations(stationsRes.data);

      if (genres.length === 0) {
        const genresRes = await api.getGenres();
        setGenres(genresRes.data.slice(0, 12));
      }

      if (user && favorites.length === 0) {
        const favRes = await api.getFavorites();
        setFavorites(favRes.data.map((s) => s.station_id));
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterStations = () => {
    let filtered = stations;

    if (search) {
      filtered = filtered.filter(
        (s) =>
          s.name.toLowerCase().includes(search.toLowerCase()) ||
          s.country.toLowerCase().includes(search.toLowerCase()) ||
          (s.city && s.city.toLowerCase().includes(search.toLowerCase()))
      );
    }

    setFilteredStations(filtered);
  };

  const handleFavorite = async (stationId) => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      if (favorites.includes(stationId)) {
        await api.removeFavorite(stationId);
        setFavorites(favorites.filter((id) => id !== stationId));
      } else {
        await api.addFavorite(stationId);
        setFavorites([...favorites, stationId]);
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  const trendingStations = filteredStations
    .slice()
    .sort((a, b) => b.play_count - a.play_count)
    .slice(0, 6);

  const newStations = filteredStations
    .slice()
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 6);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pb-40 md:pb-28">
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <h1
            className="text-4xl sm:text-5xl tracking-tighter font-black text-white mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {t.discoverTitle}
          </h1>

          <p className="text-base text-gray-300">{t.discoverSubtitle}</p>
        </div>

        {/* Search & Filter */}
        <div className="mb-12 space-y-6">
          <div className="relative">
            <MagnifyingGlass
              size={20}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            />

            <Input
              data-testid="search-input"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t.searchPlaceholder}
              className="pl-12 bg-[#0A0E17] border-white/10 text-white h-14 rounded-2xl focus:border-cyan-500 focus:ring-cyan-500/20 text-base"
            />
          </div>

          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            <button
              data-testid="genre-filter-all"
              onClick={() => setSelectedGenre('all')}
              className={`px-6 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${selectedGenre === 'all'
                  ? 'bg-cyan-500 text-black shadow-[0_0_10px_rgba(0,255,255,0.3)]'
                  : 'bg-white/5 text-gray-300 hover:bg-white/10'
                }`}
            >
              {t.allGenres}
            </button>

            {genres.map((genre) => (
              <button
                key={genre.slug}
                data-testid={`genre-filter-${genre.slug}`}
                onClick={() => setSelectedGenre(genre.name)}
                className={`px-6 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${selectedGenre === genre.name
                    ? 'bg-cyan-500 text-black shadow-[0_0_10px_rgba(0,255,255,0.3)]'
                    : 'bg-white/5 text-gray-300 hover:bg-white/10'
                  }`}
              >
                {genre.name}
              </button>
            ))}
          </div>
        </div>

        {/* Trending Stations */}
        {trendingStations.length > 0 && (
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <Fire size={32} weight="duotone" className="text-cyan-400" />

              <h2
                className="text-2xl sm:text-3xl tracking-tight font-bold text-white"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {t.trendingNow}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {trendingStations.map((station) => (
                <StationCard
                  key={station.station_id}
                  station={station}
                  onClick={() => navigate(`/station/${station.station_id}`)}
                  isFavorite={favorites.includes(station.station_id)}
                  onFavorite={handleFavorite}
                  onPlay={playStation}
                  isCurrentlyPlaying={
                    currentStation?.station_id === station.station_id &&
                    isPlaying
                  }
                  t={t}
                />
              ))}
            </div>
          </section>
        )}

        {/* New Stations */}
        {newStations.length > 0 && (
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <Sparkle size={32} weight="duotone" className="text-cyan-400" />

              <h2
                className="text-2xl sm:text-3xl tracking-tight font-bold text-white"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {t.newStations}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {newStations.map((station) => (
                <StationCard
                  key={station.station_id}
                  station={station}
                  onClick={() => navigate(`/station/${station.station_id}`)}
                  isFavorite={favorites.includes(station.station_id)}
                  onFavorite={handleFavorite}
                  onPlay={playStation}
                  isCurrentlyPlaying={
                    currentStation?.station_id === station.station_id &&
                    isPlaying
                  }
                  t={t}
                />
              ))}
            </div>
          </section>
        )}

        {/* All Stations */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <Globe size={32} weight="duotone" className="text-cyan-400" />

            <h2
              className="text-2xl sm:text-3xl tracking-tight font-bold text-white"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {t.allStations}
            </h2>
          </div>

          {filteredStations.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-400">{t.noStationsFound}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {filteredStations.map((station) => (
                <StationCard
                  key={station.station_id}
                  station={station}
                  onClick={() => navigate(`/station/${station.station_id}`)}
                  isFavorite={favorites.includes(station.station_id)}
                  onFavorite={handleFavorite}
                  onPlay={playStation}
                  isCurrentlyPlaying={
                    currentStation?.station_id === station.station_id &&
                    isPlaying
                  }
                  t={t}
                />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default Discover;